(() => {
    const saveUserResultMessageMap = {
        "Unknown": "An unknown error has occurred. Please contact support.",
        "Success": "The user was successfully saved.",
        "UserDoesNotExist": "The user could not be found.",
        "EmailAlreadyAssignedToUser": "The provided email is already assigned to an existing user.",
        "UsernameAlreadyAssignedToUser": "The provided user name is already assigned to an existing user.",
        "InvalidEmailFormat": "The provided email address is invalid.",
        "InvalidPassword": "The provided password is invalid."
    };

    class ManageUserController {
        _rolesToAdd = [];
        _rolesToDelete = [];
        email;
        username;
        firstName;
        lastName;
        mobileNumber;
        isLockedOut;
        groups = [];

        constructor(
            $scope,
            $state,
            $stateParams,
            $timeout,
            $uibModal,
            $log,
            notify,
            APIService,
            PermissionService,
            UsersService,
            ErrorService
        ) {
            this._logger = $log;
            this._apiService = APIService;
            this._permissionService = PermissionService;
            this._usersService = UsersService;
            this._errorService = ErrorService;
            this._notify = notify;
            this._userId = $stateParams.id;
            this.isExistingUser = !!this._userId;
            this._scope = $scope;
            this._state = $state;
            this._timeout = $timeout;
            this._modal = $uibModal;
            this._context = PermissionService.userContext;
            $scope.contextName = this._context.getCurrentContextName();
            $scope.mobilePattern = /^\+?[\d]{8,15}$/i;
            $scope.saveUser = this.saveButtonClicked.bind(this);
            $scope.sendResetPasswordRequest = this.sendResetPasswordRequest.bind(this);
            $scope.onRolesChanged = this.onRolesChanged.bind(this);
            $scope.nonVisibleRoles = [Dashboard.Models.RoleLookup.ReportioUser];
            $scope.isEditingUserDetailsDisabled = false;
            $scope.isEmailEditingDisabled = false;
            $scope.showNoRolesSelectedMessage = false;
            $scope.existingUserModalShownRecently = false;
            this.setEmailEditingDisabledState();
            this.checkUserAccessToUserManagement();
            this.setHeading();
            this.loadData().catch((e) => {
                this._errorService.showError("There was an issue loading the page", e)
            });
        }

        setHeading() {
            if (this.isExistingUser) {
                this._scope.manageUserHeading = "Edit User";
                return;
            }
            this._scope.manageUserHeading = "Add a User";
        }

        async loadData() {
            this._scope.busy = true;
            await Promise.all([
                await this.fetchUser(),
                await this.fetchUserBrands(),
                await this.fetchManageableRoles()
            ]);
            this.setGroupsForCallCenterContext()
            await this.fetchUserCallCenters();
            await this.populateRolesFromUser();
            this._timeout(this.updateUI.bind(this))
            this._scope.busy = false;
        }

        async getUserByEmail() {
            if (!this.email) {
                return;
            }
            if (this._scope.userInfo.email.$invalid) {
                return;
            }
            try {
                const {data: user} = await this._usersService.getUserByEmail(this.email);
                this.showExistingUserModal(
                    user.Id,
                    user.FirstName,
                    user.LastName);
            } catch (e) {
                // Fail silently. The user was not found
                this._timeout(() => this.username = this.email)
            }
        }

        setGroupsForCallCenterContext() {
            if (this._context.isCallCenterContext()) {
                this.groups = [...this._context.CallCenterGroups || []];
            }
        }

        updateUI() {
            this.firstName = this._user.FirstName;
            this.lastName = this._user.LastName;
            this.email = this._user.Email;
            this.username = this._user.Username;
            this.mobileNumber = this._user.MobileNumber;
            this.isLockedOut = this._user.IsLockedOut;
        }

        async fetchUser() {
            this._user = new User();
            if (!this.isExistingUser) {
                return;
            }
            try {
                const results = await this._usersService.getUserById(this._userId);
                this._user = new User().initialiseUser(results.data);
                this._scope.isEditingUserDetailsDisabled = this._user.IsUserEditable !== undefined
                    ? !this._user.IsUserEditable
                    : false;
                this.setEmailEditingDisabledState();
            } catch (e) {
                this._logger.error(e);
                this._errorService.showError(
                    "An error occurred while trying to fetch user data",
                    e
                );
            }
        }

        async fetchUserBrands() {
            const results = await this._usersService.getUserBrands();
            this._userBrands = results;
            if (this._context.ContextType === Dashboard.ContextTypes.StoreContext) {
                var storeBrandId = this._permissionService.getBrandIdForStoreRole(this._context.StoreId);
                this._userBrands = results.filter(brand => brand.Id === storeBrandId);
            } else if (this._context.ContextType === Dashboard.ContextTypes.BrandContext) {
                this._userBrands = results.filter(brand => brand.Id === this._context.BrandId);
            }
        }

        async fetchUserCallCenters() {
            const callCenterIds = this._user.Roles.filter(filteredRole => filteredRole || filteredRole.CallCenterId)
                .map(mappedRole => mappedRole.CallCenterId);
            this._userCallCenters = await this._usersService.getUserCallCenters(callCenterIds);
        }

        async fetchManageableRoles() {
            this._manageableRoles = await this._usersService.getRolesUserIsAllowedToManage(this._context);
        }

        getRoleToCopyContextFrom(manageableRoleWithContextFlags) {
            const roles = Array.from(
                new Set(
                    this._permissionService.getRolesForPermission(
                        Dashboard.Models.Lookups.Permissions.ManageUsers
                    ).concat(
                        this._permissionService.getRolesForPermission(
                            Dashboard.Models.Lookups.Permissions.ManageNonAdminUsers
                        )
                    )
                )
            );
            const rolesWithoutSystemAdministrativeRoles = roles
                .filter(x => x.RoleId !== Dashboard.Models.RoleLookup.SystemAdministrator)
                .filter(x => x.RoleId !== Dashboard.Models.RoleLookup.OperationsAdministrator);

            const contextFilteredRoles = rolesWithoutSystemAdministrativeRoles.filter(x => {
                if (this._context.isBrandContext()) {
                    return this._context.BrandId === x.BrandId;
                }
                if (this._context.isCallCenterContext()) {
                    return this._context.CallCenterId === x.CallCenterId;
                }
                if (this._context.isStoreContext()) {
                    return this._context.StoreId === x.StoreId;
                }
                return true;
            });

            if (!manageableRoleWithContextFlags) {
                return contextFilteredRoles[0];
            }

            const {
                brandContextRequired,
                groupContextRequired,
                callCenterContextRequired,
                storeContextRequired
            } = manageableRoleWithContextFlags;
            const filteredRoles = contextFilteredRoles.filter(x => {
                if (storeContextRequired && !x.StoreId) {
                    return false;
                }
                if (brandContextRequired && !x.BrandId && !x.StoreId) {
                    return false;
                }
                if (groupContextRequired && !x.GroupId) {
                    return false;
                }
                if (callCenterContextRequired && !x.CallCenterId) {
                    return false;
                }
                return true;
            });
            return filteredRoles[0];
        }

        async populateRolesFromUser() {
            this._scope.userManageableRoles = [];
            const storeToBrandLookup = await this._fetchStoreToBrandLookup()
            for (const role of this._manageableRoles) {
                const roleForContext = this.getRoleToCopyContextFrom(role);
                if (!roleForContext) {
                    console.error(
                        `can't find role to copy context from, role will not be assignable
this is likely a role assignment issue - store-level roles require
the current user to have one of the following permissions _at_that_store_:
- ManageUsers
- ManageNonFinanceUsers`.trim(), role);
                    continue;
                }
                const newRole = new Role().initialiseRole(role);
                newRole.setContextFromRole(roleForContext, storeToBrandLookup);
                newRole.selected = !!this._user.Roles.find(userRole => {
                    if (newRole.hasContext() && this._context.isCallCenterContext()) {
                        return userRole.RoleId === role.Id && userRole.CallCenterId === this._context.CallCenterId;
                    }
                    if (newRole.hasContext() && this._context.isStoreContext()) {
                        return userRole.RoleId === role.Id && userRole.StoreId === this._context.StoreId;
                    }
                    return userRole.RoleId === role.Id;
                });
                newRole.Name = this._permissionService.getRoleNameById(role.Id);
                this._scope.userManageableRoles.push(newRole);
            }
            this.updateRolesToAdd();
        }

        async _fetchStoreToBrandLookup() {
            const storeIds = (this._manageableRoles || [])
                .filter(o => !!o.StoreId && !o.BrandId)
                .map(o => o.StoreId);
            if (this._context.StoreId) {
                storeIds.push(this._context.StoreId);
            }
            const
                apiResult = await this._apiService.Stores.getStoresByStoreIds(storeIds),
                stores = apiResult.data;
            return stores.reduce((acc, cur) => {
                acc[cur.Id] = cur.BrandId;
                return acc;
            }, {});
        }

        roleExcludedFromDisplay(roleId) {
            return this._scope.nonVisibleRoles.includes(roleId);
        }

        checkUserAccessToUserManagement() {
            if (this._permissionService.doesUserHavePermissionToEditUsers()) {
                return;
            }
            this._permissionService.navigateToAccessDenied();
        }

        onRolesChanged() {
            this.updateRolesToDelete();
            this.updateRolesToAdd();
            this._scope.showNoRolesSelectedMessage = false;
        }

        addRolesForRemainingGroups(existingRoles, role) {

            this.groups.forEach(group => {
                const exists = !!existingRoles.find(existingRole => {
                    return this._context.isCallCenterContext()
                        ? existingRole.GroupId === group.GroupId && existingRole.CallCenterId === group.CallCenterId
                        : existingRole.GroupId === group.GroupId;
                });
                if (exists) {
                    return;
                }
                this._rolesToAdd.push({
                    ...role,
                    GroupId: group.GroupId,
                    CallCenterId: group.CallCenterId,
                    UserId: this._userId
                })
            });
        }

        updateRolesToAdd() {
            this._rolesToAdd = [];
            this._scope.userManageableRoles = this._scope.userManageableRoles.filter(f => {
                return !this.roleExcludedFromDisplay(f.Id)
            });

            this._scope.userManageableRoles.forEach(role => {
                if (!role.selected) {
                    return;
                }
                const existingRoles = this._user.Roles.filter(userRole => userRole.RoleId === role.RoleId);
                const filteredExistingRoles = existingRoles.filter(x => {
                    if (this._context.isCallCenterContext()) {
                        return this._context.CallCenterId === x.CallCenterId;
                    }
                    if (this._context.isStoreContext()) {
                        return this._context.StoreId === x.StoreId;
                    }
                    return true;
                });

                if (role.groupContextRequired) {
                    return this.addRolesForRemainingGroups(filteredExistingRoles, role);
                }
                if (filteredExistingRoles.length > 0) {
                    return;
                }
                this._rolesToAdd.push({
                    ...role,
                    UserId: this._userId
                });
            })
        }

        updateRolesToDelete() {
            this._rolesToDelete = [];
            this._scope.userManageableRoles.forEach(role => {
                if (role.selected) {
                    return;
                }
                const existingRoles = this._user.Roles.filter(userRole => {
                    const newRole = new Role().initialiseRole(role);
                    if (newRole.hasContext() && this._context.isCallCenterContext()) {
                        return userRole.RoleId === role.RoleId
                            && userRole.CallCenterId === this._context.CallCenterId;
                    }
                    if (newRole.hasContext() && this._context.isStoreContext()) {
                        return userRole.RoleId === role.RoleId
                            && userRole.StoreId === this._context.StoreId;
                    }
                    return userRole.RoleId === role.RoleId
                });
                existingRoles.forEach(existingRole => {
                    this._rolesToDelete.push({
                        ...existingRole,
                        UserId: this._userId
                    });
                });
            });
        }

        async updateUserRoles(userId) {
            if (this._rolesToAdd.length > 0) {
                await this._usersService.addUserRoles(
                    userId,
                    this._rolesToAdd
                );
            }
            if (this._rolesToDelete.length > 0) {
                await this._usersService.deleteUserRoles(
                    userId,
                    this._rolesToDelete,
                );
            }
        }

        showExistingUserModal(
            userId,
            firstName,
            lastName
        ) {
            if (this._scope.existingUserModalShownRecently) {
                return;
            }
            this._scope.existingUserModalShownRecently = true;
            const modalTemplateUrl = "components/users/manageuser/modals/editexistingusermodal.aa38056f.html";
            this._modal.open({
                animation: true,
                templateUrl: modalTemplateUrl,
                controller: 'EditExistingUserModalController',
                resolve: {
                    userId: () => userId,
                    firstName: () => firstName,
                    lastName: () => lastName,
                    manageUserById: () => this.manageUser.bind(this),
                    resetEmailInput: () => this.resetEmailInputField.bind(this)
                }
            });
            setTimeout(
                () => this._scope.existingUserModalShownRecently = false,
                2000);
        }

        resetEmailInputField() {
            this.email = "";
            this._timeout(() => {
                angular.element("#email").focus();
            });
        }

        async manageUser(userId) {
            this._state.go(
                Dashboard.helpers.stateHelper.getParentState(this._state.current.name)
                + ".edituser", {
                    id: userId,
                    brandId: this._context.BrandId,
                    storeId: this._context.StoreId,
                    callCenterId: this._context.CallCenterId
                });
        }

        rolesWithContextAvailable() {
            return !!this._scope.userManageableRoles.find(role => role.hasContext() ? role.selected : false);
        }

        saveButtonClicked() {
            if (this._scope.busy) {
                return;
            }
            this._scope.submitted = true;
            if (!this._scope.userInfo.$valid) {
                return;
            }
            if (!this.isExistingUser) {
                return this.createUser();
            }
            const shouldShowCallCenterRoleConfirmModal = this.shouldShowCallCenterRoleConfirmationModal();
            if (shouldShowCallCenterRoleConfirmModal) {
                return this.showSwitchCallCenterRolesConfirmationModal();
            }
            const shouldProceedWithoutPrompt = this.rolesWithContextAvailable();
            if (shouldProceedWithoutPrompt) {
                return this.save();
            }
            this.showModalForNoRolesForExistingUser();
        }

        shouldShowCallCenterRoleConfirmationModal() {
            if (this._rolesToAdd.length === 0) {
                return false;
            }
            const callCenterIds = this._userCallCenters.map(mappedCallCenter => mappedCallCenter.Id);
            const callCenterAgentRoles = this._rolesToAdd
                .filter(filteredRole => filteredRole.RoleId === Dashboard.Models.RoleLookup.CallCenterAgent);
            return this.checkUserHasCallCenterAgentRolesInMultipleCallCenters(callCenterAgentRoles, callCenterIds);
        }

        checkUserHasCallCenterAgentRolesInMultipleCallCenters(callCenterAgentRoles, callCenterIds) {
            if (callCenterAgentRoles.length === 0) {
                return false;
            }
            return callCenterIds.some(someId =>
                callCenterAgentRoles.some(agentRole => agentRole.CallCenterId !== someId));
        }

        getReadableListOfOtherCallCenterNames() {
            const otherCallCenterNames = this._userCallCenters
                .filter(filteredCallCenter => filteredCallCenter.Id !== this._context.CallCenterId)
                .map(mappedCallCenter => mappedCallCenter.Name);
            if (!otherCallCenterNames || otherCallCenterNames.length === 0) {
                return "";
            }
            if (otherCallCenterNames.length > 1) {
                const partial = otherCallCenterNames.slice(0, otherCallCenterNames.length - 1);
                const last = otherCallCenterNames.slice(-1);
                return `${partial.join(", ")} and ${last[0]}`
            }
            return otherCallCenterNames[0];
        };

        showModalForNoRolesForExistingUser() {
            const modalTemplateUrl = "components/users/manageuser/modals/removeallrolesmodal.c780c87a.html";
            this._modal.open({
                animation: true,
                templateUrl: modalTemplateUrl,
                controller: "RemoveAllRolesModalController",
                size: "lg",
                backdrop: "static",
                resolve: {
                    contextName: () => this._context.getCurrentContextName(),
                    firstName: () => this.firstName,
                    lastName: () => this.lastName,
                    proceed: () => this.save.bind(this)
                }
            })
        }

        showModalForNoRolesForNewUser() {
            const modalTemplateUrl = "components/users/manageuser/modals/removeallrolesmodalfornewuser.c106583b.html";
            this._modal.open({
                animation: true,
                templateUrl: modalTemplateUrl,
                controller: "RemoveAllRolesModalController",
                size: "lg",
                backdrop: "static",
                resolve: {
                    contextName: () => this._context.getCurrentContextName(),
                    firstName: () => this.firstName,
                    lastName: () => this.lastName,
                    proceed: () => this.save.bind(this)
                }
            })
        }

        showCreateUserConfirmationDialog(userId, firstName, lastName) {
            const modalTemplateUrl = "components/users/manageuser/modals/usercreatedmodal.f6044b51.html";

            this._modal.open({
                animation: true,
                templateUrl: modalTemplateUrl,
                controller: 'UserCreatedModalController',
                backdrop: 'static',
                resolve: {
                    userId: () => userId,
                    firstName: () => firstName,
                    lastName: () => lastName,
                    manageUserById: () => this.manageUser.bind(this)
                }
            });
        }

        showSwitchCallCenterRolesConfirmationModal() {
            const modalTemplateUrl = "components/users/manageuser/modals/switchcallcenterrolesmodal.2ba3557e.html";
            this._modal.open({
                animation: true,
                templateUrl: modalTemplateUrl,
                controller: 'SwitchCallCenterRolesModalController',
                backdrop: 'static',
                resolve: {
                    currentCallCenterName: () => this._context.getCurrentContextName(),
                    otherCallCenterNames: () => this.getReadableListOfOtherCallCenterNames(),
                    proceed: () => this.save.bind(this)
                }
            });
        }

        async createUser() {
            const userModel = {
                Username: this.username,
                FirstName: this.firstName,
                LastName: this.lastName,
                Email: this.email,
                MobileNumber: this.mobileNumber,
                IsLocked: this.isLockedOut,
                IsActive: true
            }
            try {
                this._scope.busy = true;
                const areRolesSelected = this._scope.userManageableRoles.filter(ur => ur.selected).length > 0;
                if (!areRolesSelected
                    && !this.isExistingUser) {
                    this._scope.showNoRolesSelectedMessage = true;
                    return;
                }
                const response = await this._usersService.createUser(userModel);
                const {UserId, Result} = response.data;
                if (!Result
                    || Result.toLowerCase() !== "success") {
                    return;
                }
                this._rolesToAdd = this._rolesToAdd.map(role => ({...role, UserId}))
                await this.updateUserRoles(UserId);
                this.showCreateUserConfirmationDialog(
                    UserId,
                    userModel.FirstName,
                    userModel.LastName
                );
                this.navigateToUsersList();
            } catch (e) {
                this._errorService.showError("An error occurred when trying to add the user", e);
                console.error(e);
            } finally {
                this._scope.busy = false;
            }
        }

        async save() {
            const {IsActive, Username, Email} = this._user;
            const userModel = {
                Id: this._userId,
                FirstName: this.firstName,
                LastName: this.lastName,
                IsLocked: this.isLockedOut,
                MobileNumber: this.mobileNumber,
                Email,
                Username,
                IsActive,
            };
            try {
                this._scope.busy = true;
                if (this.haveUserDetailsChanged()) {
                    const saveResult = await this._usersService.saveUser(userModel);
                    const result = saveResult?.data?.Result;
                    if (result !== "Success") {
                        this._notify.error(
                            saveUserResultMessageMap[result] ||
                            saveUserResultMessageMap["Unknown"]
                        );
                        return;
                    } else {
                        this._notify.success(
                            saveUserResultMessageMap[result] ||
                            saveUserResultMessageMap["Unknown"]
                        );
                    }
                }
            } catch (e) {
                if (e.status === 403) {
                    this._errorService.showError(
                        e.data.Error,
                        e
                    );
                    return;
                }
                this._errorService.showError(
                    "An error occurred when trying to save the user",
                    e
                );
                this._scope.busy = false;
                return;
            }
            try {
                await this.updateUserRoles(this._userId)
                this._notify.success("The user roles were successfully saved");
            } catch (e) {
                this._errorService.showError(
                    "An error occurred when trying to save roles for the user",
                    e
                );
            } finally {
                await this.loadData();
                this.onRolesChanged();
                this.navigateToUsersList();
                this._scope.busy = false;
            }
        }

        haveUserDetailsChanged() {
            return this._user.FirstName !== this.firstName
                || this._user.LastName !== this.lastName
                || this._user.IsLockedOut !== this.isLockedOut
                || this._user.MobileNumber !== this.mobileNumber;
        }

        async sendResetPasswordRequest() {
            try {
                await this._usersService.requestPasswordResetForUser(this._userId);
                const modalTemplateUrl = "components/users/manageuser/modals/passwordresetmodal.dbfa9932.html";
                this._modal.open({
                    animation: true,
                    templateUrl: modalTemplateUrl,
                    controller: "PasswordResetModalController",
                    size: "lg",
                    backdrop: "static"
                });
            } catch (e) {
                this._errorService.showError("An error occurred while sending a reset password request", e);
            }
        }

        setEmailEditingDisabledState() {
            this._scope.isEmailEditingDisabled = this.isExistingUser
                || this._scope.isEditingUserDetailsDisabled;
        }

        navigateToUsersList() {
            if (this._context.isBrandContext()) {
                this._state.go(
                    "app.brandusers",
                    {
                        brandId: this._context.BrandId
                    }
                );
            }
            if (this._context.isCallCenterContext()) {
                this._state.go(
                    "app.callcenterusers",
                    {
                        callCenterId: this._context.CallCenterId
                    }
                );
            }
            if (this._context.isStoreContext()) {

                this._state.go(
                    "app.storeusers",
                    {
                        storeId: this._context.StoreId
                    }
                );
            }
        }

    }

    angular
        .module("dashboard")
        .component("manageUser",
            {
                templateUrl: "components/users/manageuser/manageuser.2242409c.html",
                bindings: {},
                controller: [
                    "$scope",
                    "$state",
                    "$stateParams",
                    "$timeout",
                    "$uibModal",
                    "$log",
                    "notify",
                    "APIService",
                    "PermissionService",
                    "UsersService",
                    "ErrorService",
                    ManageUserController
                ]
            });
})();
